/**
 * Formato chileno: miles con punto, decimales con coma, fechas dd-MM-yyyy.
 * Ver PROMPT-MAESTRO §8.2.
 */

const LOCALE = 'es-CL';

/** $ 1.234.567 */
export function clp(monto: number | null | undefined, conSimbolo = true): string {
  if (monto == null || Number.isNaN(monto)) return '—';
  const n = Math.round(monto).toLocaleString(LOCALE);
  return conSimbolo ? `$ ${n}` : n;
}

/** UF 3.106,942 — el contrato trabaja con 3 decimales. */
export function uf(monto: number | null | undefined, decimales = 3): string {
  if (monto == null || Number.isNaN(monto)) return '—';
  return monto.toLocaleString(LOCALE, {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  });
}

/** 1.234,5 m³ */
export function m3(cantidad: number | null | undefined, decimales = 1): string {
  if (cantidad == null || Number.isNaN(cantidad)) return '—';
  return cantidad.toLocaleString(LOCALE, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimales,
  });
}

/** Número genérico con separador de miles. */
export function num(n: number | null | undefined, decimales = 0): string {
  if (n == null || Number.isNaN(n)) return '—';
  return n.toLocaleString(LOCALE, {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  });
}

/** 12,3 % */
export function pct(fraccion: number | null | undefined, decimales = 1): string {
  if (fraccion == null || Number.isNaN(fraccion)) return '—';
  return `${(fraccion * 100).toLocaleString(LOCALE, {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  })} %`;
}

/** yyyy-MM-dd → 20-08-2026 */
export function fecha(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? parseFechaISO(iso) : iso;
  if (!d || Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}-${mm}-${d.getFullYear()}`;
}

/** Date → 14:35 */
export function hora(d: Date | null | undefined): string {
  if (!d || Number.isNaN(d.getTime())) return '—';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** 20-08-2026 14:35 */
export function fechaHora(d: Date | null | undefined): string {
  if (!d || Number.isNaN(d.getTime())) return '—';
  return `${fecha(d)} ${hora(d)}`;
}

/** 'yyyy-MM-dd' → Date local (evita el desfase de zona de `new Date('2026-08-20')`). */
export function parseFechaISO(iso: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** Date → 'yyyy-MM-dd' en horario local. */
export function aFechaISO(d: Date): string {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

/** agosto 2026 */
export function mesAnio(d: Date): string {
  return `${MESES[d.getMonth()]} ${d.getFullYear()}`;
}

/** Formatea un RUT chileno: 96953700-1 → 96.953.700-1 */
export function rut(valor: string | null | undefined): string {
  if (!valor) return '—';
  const limpio = valor.replace(/[^0-9kK]/g, '').toUpperCase();
  if (limpio.length < 2) return valor;
  const dv = limpio.slice(-1);
  const cuerpo = limpio.slice(0, -1);
  return `${Number(cuerpo).toLocaleString(LOCALE)}-${dv}`;
}

/** Valida el dígito verificador de un RUT chileno (módulo 11). */
export function rutValido(valor: string): boolean {
  const limpio = valor.replace(/[^0-9kK]/g, '').toUpperCase();
  if (limpio.length < 2) return false;
  const dv = limpio.slice(-1);
  const cuerpo = limpio.slice(0, -1);
  if (!/^\d+$/.test(cuerpo)) return false;

  let suma = 0;
  let multiplicador = 2;
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    suma += Number(cuerpo[i]) * multiplicador;
    multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
  }
  const resto = 11 - (suma % 11);
  const dvEsperado = resto === 11 ? '0' : resto === 10 ? 'K' : String(resto);
  return dv === dvEsperado;
}
